import { getOutputDomainRecommendations } from '../transforms/son-ownership.transform';
import { VCPM } from '../models/project.model';
import { VcfSizingOutput } from '../models/vcf-sizing-output.model';
import { PcsSizingEngineResponse } from '../models/pcs-sizing-engine-response.model';
import { AppConstants } from '../models/app-constants.model';
import { getFlatStretchedMultiplier } from '../transforms/stretched-cluster.transform';
import { getRecommendedProfileCode, READY_NODE_PROFILE_SIZE_LABELS } from './sizing-recalculation.adapter';

/**
 * Resolved by the caller from Angular-only sources (NGXS store, AuthService) before calling
 * buildReportPayload. See plan Section 2.1 / Gap H.
 *
 * Model gap (not fixed): the plan's placeholder shape (`{ username?, acct?, email? }`) does not
 * match the 3 fields actually read from AuthService.profile$.value in the original service
 * (`name`, `email`, `user_loginid`) - pinned to the real fields here instead.
 */
export interface ReportPayloadContext {
  user: { name?: string; email?: string; user_loginid?: string };
  savedSummary?: VcfSizingOutput.Summary;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ReportPayload = any;

/**
 * Distinguishes, for the exported report, which of the three states a workload's active sizing is
 * actually in - the report label previously could only tell "estimate" (isEstimate) from "exact"
 * (a real ReadyNode systemId), so an explicitly saved NON-recommended tier (PCS-2516/PCS-2518) was
 * indistinguishable from the plain engine recommendation and rendered as "Recommended", which is
 * factually wrong once the user has overridden it. `type: 'PROFILE'` is the new, third case;
 * RECOMMENDED and READYNODE are informational only - the existing isEstimate/systemId-driven
 * template logic already renders those two correctly and is kept as the fallback (see the vcf-tools-
 * report JSONata templates), so this field's only real job is to flag the PROFILE case precisely.
 */
export interface SizingBasis {
  type: 'RECOMMENDED' | 'READYNODE' | 'PROFILE';
  profileCode?: string;
  profileLabel?: string;
}

/**
 * Resolves the same three-way distinction workload-domain.component.ts/management-domain.component.ts
 * use for the profile picker's own highlighting (see getEffectiveSelectedProfileCode), reusing
 * getRecommendedProfileCode as the single source of truth for "what the engine recommended" rather
 * than re-deriving it here.
 */
export function resolveSizingBasis(
  workloadRecomm: PcsSizingEngineResponse.WorkloadRecommendation,
  inputHardwareConfigMode: string | undefined,
): SizingBasis {
  const selected = workloadRecomm.selectedProfileCode;
  const recommendedCode = getRecommendedProfileCode(workloadRecomm, inputHardwareConfigMode);
  // Mirrors getEffectiveSelectedProfileCode() in workload-domain.component.ts/management-domain.
  // component.ts: an explicit pick wins, else fall back to the recommended default - which, for a
  // readynode-based workload with alternate tiers, can itself BE the sentinel (no tier is ever
  // genuinely "recommended" once a specific ReadyNode has been chosen upfront).
  const effectiveCode = selected ?? recommendedCode;
  if (effectiveCode === AppConstants.SELECTED_READYNODE_PROFILE_CODE) {
    return { type: 'READYNODE' };
  }
  if (selected && selected !== recommendedCode) {
    return { type: 'PROFILE', profileCode: selected, profileLabel: READY_NODE_PROFILE_SIZE_LABELS[selected] ?? selected };
  }
  return { type: 'RECOMMENDED' };
}

/**
 * Swaps in the sizing of a SAVED ReadyNode so the exported report matches what the user is looking
 * at on screen.
 *
 * A workload sized against a specific ReadyNode keeps that result on
 * `selectedReadyNodeRecommendation.result` - the recommendation's OWN top-level `clusters` remain
 * the engine's original (pre-ReadyNode) sizing. Every on-screen card resolves through
 * getEffectiveWorkloadRecommendation() and so shows the ReadyNode's real numbers, but this adapter
 * serialized the raw clusters, so the PPT reported the wrong hosts/cores/memory/drives AND a
 * generic chassis with no ReadyNode identity at all - the report simply never reflected a saved
 * ReadyNode (PCS-2518).
 *
 * Deliberately scoped to the ReadyNode case ONLY: a saved tier pick also resolves to a different
 * candidate's clusters on screen (see getEffectiveWorkloadRecommendation), but changing that here
 * would alter the numbers exported for every tier-sized workload, so it is left alone pending its
 * own verification.
 *
 * Identity (`name`, `workloadId`) and the `sizingBasis` label stay on the raw recommendation; only
 * the three sizing-derived fields the PPT templates actually read - `clusters`, `profileCode`,
 * `isEstimate` - come from the ReadyNode result. `isEstimate` matters as much as the numbers: left
 * at the raw recommendation's `true`, the template titles the slide "Recommended Node Configuration
 * Details" even though an exact ReadyNode was chosen.
 */
function withSavedReadyNodeSizing(
  workloadRecomm: PcsSizingEngineResponse.WorkloadRecommendation,
): PcsSizingEngineResponse.WorkloadRecommendation {
  if (workloadRecomm.selectedProfileCode !== AppConstants.SELECTED_READYNODE_PROFILE_CODE) {
    return workloadRecomm;
  }
  const readyNodeResult = workloadRecomm.selectedReadyNodeRecommendation?.result;
  if (!readyNodeResult?.clusters?.length) {
    return workloadRecomm;
  }
  return {
    ...workloadRecomm,
    clusters: readyNodeResult.clusters,
    profileCode: readyNodeResult.profileCode ?? workloadRecomm.profileCode,
    isEstimate: readyNodeResult.isEstimate ?? workloadRecomm.isEstimate,
  };
}

/**
 * Ported as-is from ReportServiceRequestAdapterService.getReportServiceRequest
 * (report-service-request-adapter.service.ts:19-94).
 */
export function buildReportPayload(project: VCPM.Project, context: ReportPayloadContext): ReportPayload {
  const savedSummary = context.savedSummary;
  const user = context.user;

  // Normalize workloadDomains and managementDomain to handle customDriveSize
  const normalizedWorkloadDomains = normalizeWorkloadDomains(project.data.workloadDomains);
  const normalizedManagementDomain = normalizeManagementDomain(project.data.managementDomain);
  const normalizedStorageClusters = normalizeStorageClusters(project.data.clusterConfigurations);

  // Determine template version based on project version
  const templateVersion = getTemplateVersion(project.metadata.version);

  return {
    template: {
      templateId: `vcf-${templateVersion}-executive-summary`,
      mappingRulesId: `vcf-${templateVersion}-executive-summary-mapping-rules`,
    },
    data: {
      projectId: project.projectId,
      lockedBy: project.lockedBy,
      createdDate: project.createdDate,
      createdBy: project.createdBy,
      lastUpdate: project.lastUpdate,
      updatedBy: project.updatedBy,
      sharingData: project.sharingData,
      primaryVersion: project.primaryVersion,
      currentOwner: project.currentOwner,
      isarchived: project.isarchived,
      version: project.version,
      active: project.active,
      locked: project.locked,
      userFullName: user.name || user.email || user.user_loginid || '',
      data: {
        output: {
          recommendation: {
            summary: {
              numberOfVMs: savedSummary?.vcfDeploymentInfraSummary.vms.value,
              numberOfHosts: savedSummary?.vcfDeploymentInfraSummary.hosts.value,
              numberOfRacks: 1,
              numberOfClusters: 1,
              numberofTorSwitch: 2,
              numberOfInterRackSwitch: 0,
              numberOfManagementSwitch: 1,
              memory: {
                units: 'TB',
                value: roundToTwo(parseFloat(String(savedSummary?.vcfDeploymentInfraSummary.memory.value))),
              },
              rawCapacity: {
                units: 'TB',
                value: roundToTwo(parseFloat(String(savedSummary?.vcfDeploymentInfraSummary.rawStorage.value))),
              },
              effectiveCapacity: {
                units: 'TB',
                value: roundToTwo(parseFloat(String(savedSummary?.vcfDeploymentInfraSummary.usableStorage?.value))),
              },
              cores: {
                value: roundToTwo(parseFloat(String(savedSummary?.vcfDeploymentInfraSummary.cores.value))),
              },
              remainingFreeSpace: {
                units: 'TB',
                value: roundToTwo(parseFloat(String(savedSummary?.productInfo.availableStorageEntitlement?.value))),
              },
            },
            workloadDomainsRecommendation: {
              result: {
                id: project?.data.output?.recommendation?.workloadDomainsRecommendation?.result.id,
                production: project?.data.output?.recommendation?.workloadDomainsRecommendation?.result.production,
                sizingAssumptions: project?.data.output?.recommendation?.workloadDomainsRecommendation?.result.sizingAssumptions,
                mgmtComponentsSuggestions: project?.data.output?.recommendation?.workloadDomainsRecommendation?.result.mgmtComponentsSuggestions,
                recommendations: addSummaryToWorkloadDomainRecommendation(
                  getOutputDomainRecommendations(project),
                  savedSummary?.workloadDomains,
                  project.data.workloadDomains,
                ),
              },
            },
            managementDomainRecommendation: addSummaryToMgmtDomainRecommendation(
              project?.data.output?.recommendation?.managementDomainRecommendation,
              savedSummary?.managementDomain,
              project.data.managementDomain?.configuration?.hardwareConfiguration?.hardwareConfigMode,
            ),
          },
        },
        workloadDomains: normalizedWorkloadDomains,
        managementDomain: normalizedManagementDomain,
        ...(normalizedStorageClusters && { clusterConfigurations: normalizedStorageClusters }),
      },
      metadata: project.metadata,
    },
  };
}

/**
 * Ported as-is from ReportServiceRequestAdapterService.addSummaryToMgmtDomainRecommendation
 * (report-service-request-adapter.service.ts:96-166). Consumes S8's getFlatStretchedMultiplier
 * instead of the inline `isStretched ? 2 : 1` (plan Section S3).
 */
export function addSummaryToMgmtDomainRecommendation(
  managementDomainRecommendation: PcsSizingEngineResponse.RecommendationResponse | undefined,
  managementDomainSummary: VcfSizingOutput.ManagementDomain | undefined,
  inputHardwareConfigMode?: string,
) {
  if (!managementDomainRecommendation) return managementDomainRecommendation;
  const clonedRecomm: PcsSizingEngineResponse.RecommendationResponse = JSON.parse(JSON.stringify(managementDomainRecommendation));

  const isStretched = managementDomainSummary?.isStretched;
  const multiplier = getFlatStretchedMultiplier(!!isStretched);
  const domainSummarySource = clonedRecomm.result.recommendations[0].domainSummary;

  const memoryValue = managementDomainSummary?.domainInfraSummary.memory.value;
  const rawCapacityValue = managementDomainSummary?.domainInfraSummary.rawStorage.value;
  const effectiveCapacityValue = managementDomainSummary?.domainInfraSummary.usableStorage?.value;
  const coresValue = managementDomainSummary?.domainInfraSummary.cores.value;

  const doubledComponents = clonedRecomm.result.recommendations[0].components.map((comp) => {
    if (multiplier === 2 && comp.reservation?.vms && (comp.placementType === 'MANAGEMENT' || comp.placementType === 'WORKLOAD')) {
      return {
        ...comp,
        reservation: {
          ...comp.reservation,
          vms: comp.reservation.vms * multiplier,
        },
      };
    }
    return comp;
  });

  return {
    result: {
      id: clonedRecomm.result.id,
      production: clonedRecomm.result.production,
      recommendations: [
        {
          type: clonedRecomm.result.recommendations[0].type,
          errors: [],
          components: doubledComponents,
          domainType: 'MANAGEMENT',
          domainSummary: {
            numberOfVMs: domainSummarySource?.numberOfVMs,
            numberOfHosts: domainSummarySource?.numberOfHosts,
            numberOfRacks: domainSummarySource?.numberOfRacks,
            numberOfClusters: domainSummarySource?.numberOfClusters,
            numberofTorSwitch: domainSummarySource?.numberofTorSwitch,
            numberOfInterRackSwitch: domainSummarySource?.numberOfInterRackSwitch,
            numberOfManagementSwitch: domainSummarySource?.numberOfManagementSwitch,
            memory: {
              units: managementDomainSummary?.domainInfraSummary.memory.unit,
              value: memoryValue != null ? roundToTwo(parseFloat(String(memoryValue)) * multiplier) : memoryValue,
            },
            rawCapacity: {
              units: managementDomainSummary?.domainInfraSummary.rawStorage.unit,
              value: rawCapacityValue != null ? roundToTwo(parseFloat(String(rawCapacityValue)) * multiplier) : rawCapacityValue,
            },
            effectiveCapacity: {
              units: managementDomainSummary?.domainInfraSummary.usableStorage?.unit,
              value: effectiveCapacityValue != null ? roundToTwo(parseFloat(String(effectiveCapacityValue)) * multiplier) : effectiveCapacityValue,
            },
            cores: {
              value: coresValue != null ? roundToTwo(parseFloat(String(coresValue)) * multiplier) : coresValue,
            },
          },
          requirementsId: clonedRecomm.result.recommendations[0].requirementsId,
          recommendationId: clonedRecomm.result.recommendations[0].recommendationId,
          requirementsName: clonedRecomm.result.recommendations[0].requirementsName,
          workloadDomainType: clonedRecomm.result.recommendations[0].workloadDomainType,
          // sizingBasis reads selectedProfileCode off the RAW recommendation; the clusters then come
          // from a saved ReadyNode's own result when there is one - see withSavedReadyNodeSizing.
          workloadRecommendations: clonedRecomm.result.recommendations[0].workloadRecommendations.map((rawWr) => {
            const wr = withSavedReadyNodeSizing(rawWr);
            return {
              ...wr,
              clusters: wr.clusters.map((c) => roundClusterNumerics(c)),
              sizingBasis: resolveSizingBasis(rawWr, inputHardwareConfigMode),
            };
          }),
        },
      ],
      sizingAssumptions: clonedRecomm.result.sizingAssumptions,
      mgmtComponentsSuggestions: clonedRecomm.result.mgmtComponentsSuggestions,
    },
  };
}

/**
 * Ported as-is from ReportServiceRequestAdapterService.addSummaryToWorkloadDomainRecommendation
 * (report-service-request-adapter.service.ts:168-249). Consumes S8's getFlatStretchedMultiplier
 * instead of the inline `clusterType === 'stretched' ? 2 : 1` (plan Section S3).
 */
export function addSummaryToWorkloadDomainRecommendation(
  workloadDomainRecommendations: Array<PcsSizingEngineResponse.Recommendation> | undefined,
  workloadDomainSummary: Array<VcfSizingOutput.WorkloadDomain> | undefined,
  workloadDomains?: Array<VCPM.WorkloadDomain>,
) {
  if (!workloadDomainRecommendations) return workloadDomainRecommendations;
  const clonedRecommendations: Array<PcsSizingEngineResponse.Recommendation> = JSON.parse(JSON.stringify(workloadDomainRecommendations));
  clonedRecommendations.forEach((workloadDomainRecommendation, index) => {
    const matchingDomain = workloadDomainSummary?.find((ele) => ele.id === workloadDomainRecommendation.requirementsId);
    if (matchingDomain?.ownedStorageClusters?.length) {
      Object.assign(workloadDomainRecommendation, { ownedStorageClusters: JSON.parse(JSON.stringify(matchingDomain.ownedStorageClusters)) });
    }
    const workloadDomainDetails = matchingDomain?.domainDetails;
    const infra = matchingDomain?.domainInfra;
    // Deduped, ownership-aware totals (aggregateDomainInfra) win over the legacy per-workload x2 sums below,
    // which stay only for summaries saved before domainInfra existed.
    const memory = infra ? infra.memory.value : matchingDomain?.workloads.reduce((acc, curr) => {
      const m = getFlatStretchedMultiplier(curr.clusterType === 'stretched');
      return acc + +curr.workloadInfraSummary.memory.value * m;
    }, 0);
    const rawCapacity = infra ? infra.rawStorage.value : matchingDomain?.workloads.reduce((acc, curr) => {
      const m = getFlatStretchedMultiplier(curr.clusterType === 'stretched');
      return acc + +curr.workloadInfraSummary.rawStorage.value * m;
    }, 0);
    const effectiveCapacity = infra && Number(infra.usableStorage?.value) > 0 ? infra.usableStorage.value : matchingDomain?.workloads.reduce((acc, curr) => {
      const m = getFlatStretchedMultiplier(curr.clusterType === 'stretched');
      return acc + +(curr.workloadInfraSummary.usableStorage?.value ?? 0) * m;
    }, 0);
    const cores = infra ? infra.cores.value : matchingDomain?.workloads.reduce((acc, curr) => {
      const m = getFlatStretchedMultiplier(curr.clusterType === 'stretched');
      return acc + +curr.workloadInfraSummary.cores.value * m;
    }, 0);
    const hosts = infra ? infra.hosts.value : matchingDomain?.workloads.reduce((acc, curr) => {
      const m = getFlatStretchedMultiplier(curr.clusterType === 'stretched');
      return acc + +curr.workloadInfraSummary.hosts.value * m;
    }, 0);
    const vms = infra ? infra.vms.value : matchingDomain?.workloads.reduce((acc, curr) => {
      const m = getFlatStretchedMultiplier(curr.clusterType === 'stretched');
      return acc + +curr.workloadInfraSummary.vms.value * m;
    }, 0);
    let domainSummary: PcsSizingEngineResponse.DomainSummary = {} as PcsSizingEngineResponse.DomainSummary;
    if (workloadDomainDetails) {
      domainSummary = {
        numberOfVMs: vms,
        numberOfHosts: hosts,
        numberOfRacks: workloadDomainDetails.numberOfRacks,
        numberOfClusters: workloadDomainDetails.numberOfClusters,
        numberofTorSwitch: workloadDomainDetails.numberofTorSwitch,
        numberOfInterRackSwitch: workloadDomainDetails.numberOfInterRackSwitch,
        numberOfManagementSwitch: workloadDomainDetails.numberOfManagementSwitch,
        memory: {
          units: 'TB',
          value: roundToTwo(memory),
        },
        rawCapacity: {
          units: 'TB',
          value: roundToTwo(rawCapacity),
        },
        effectiveCapacity: {
          units: 'TB',
          value: roundToTwo(effectiveCapacity),
        },
        cores: {
          value: roundToTwo(cores),
        },
      };
    }
    clonedRecommendations[index].domainSummary = domainSummary;

    // Double component resources for stretched workload clusters
    const hasStretchedWorkload = matchingDomain?.workloads?.some((w) => w.clusterType === 'stretched') ?? false;
    const componentMultiplier = getFlatStretchedMultiplier(hasStretchedWorkload);

    if (componentMultiplier === 2 && workloadDomainRecommendation.components && workloadDomainRecommendation.components.length > 0) {
      workloadDomainRecommendation.components = workloadDomainRecommendation.components.map((comp) => {
        if (comp.placementType === 'WORKLOAD' && comp.reservation?.vms) {
          return {
            ...comp,
            reservation: {
              ...comp.reservation,
              vms: comp.reservation.vms * componentMultiplier,
            },
          };
        }
        return comp;
      });
    }
  });
  clonedRecommendations.forEach((workloadDomainRecommendation) => {
    delete workloadDomainRecommendation.calculationLog;
    const inputDomain = workloadDomains?.find((d) => String(d.id) === String(workloadDomainRecommendation.requirementsId));
    // sizingBasis is resolved from the RAW recommendation (it reads selectedProfileCode, which only
    // exists there), then the saved ReadyNode's own sizing replaces the clusters - see
    // withSavedReadyNodeSizing.
    workloadDomainRecommendation.workloadRecommendations = workloadDomainRecommendation.workloadRecommendations.map((rawWorkloadRecommendation) => {
      const inputWorkload = inputDomain?.workloadList?.find((w) => String(w.id) === String(rawWorkloadRecommendation.workloadId));
      const sizingBasis = resolveSizingBasis(rawWorkloadRecommendation, inputWorkload?.hardwareConfiguration?.hardwareConfigMode);
      const workloadRecommendation = withSavedReadyNodeSizing(rawWorkloadRecommendation);
      delete workloadRecommendation.calculationLog;
      workloadRecommendation.clusters.forEach((cluster) => {
        delete cluster.calculationLog;
        roundClusterNumerics(cluster);
      });
      return Object.assign(workloadRecommendation, { sizingBasis });
    });
  });
  return clonedRecommendations;
}

/**
 * Normalizes workload domains by replacing driveSize with customDriveSize when "other" is selected.
 * Module-private (not part of the barrel's public API), exported only so the SWITCH wrapper can
 * delegate to it - ported as-is from
 * ReportServiceRequestAdapterService.normalizeWorkloadDomains (report-service-request-adapter.service.ts:254-276).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function normalizeWorkloadDomains(workloadDomains: VCPM.WorkloadDomain[] | undefined): any[] | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (!workloadDomains || workloadDomains.length === 0) return workloadDomains as any;

  try {
    // Deep clone to avoid mutating original data
    const clonedDomains = JSON.parse(JSON.stringify(workloadDomains));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    clonedDomains.forEach((domain: any) => {
      if (domain.workloadList && Array.isArray(domain.workloadList)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        domain.workloadList.forEach((workload: any) => {
          if (workload?.hardwareConfiguration) {
            normalizeHardwareConfiguration(workload.hardwareConfiguration);
          }
        });
      }
    });

    return clonedDomains;
  } catch (error) {
    console.error('Error normalizing workload domains:', error);
    return JSON.parse(JSON.stringify(workloadDomains)); // Return clone without normalization on error
  }
}

/**
 * Normalizes the project-level vSAN MAX StorageClusters for the report payload: deep clones, applies
 * the customDriveSize normalization and rounds/strips the saved recalculation clusters the same way
 * the main recommendation output is. Returns undefined when there are none, so the key is omitted.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function normalizeStorageClusters(storageClusters: VCPM.StorageCluster[] | undefined): any[] | undefined {
  if (!storageClusters || storageClusters.length === 0) return undefined;

  // Deep clone to avoid mutating original data
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const clonedClusters: any[] = JSON.parse(JSON.stringify(storageClusters));
  clonedClusters.forEach((storageCluster) => {
    normalizeHardwareConfiguration(storageCluster.hardwareConfiguration);
    const result = storageCluster.lastRecalculationResult?.result;
    delete result?.calculationLog;
    result?.clusters?.forEach((cluster: PcsSizingEngineResponse.Cluster) => {
      delete cluster.calculationLog;
      roundClusterNumerics(cluster);
    });
  });
  return clonedClusters;
}

/**
 * Module-private (not part of the barrel's public API), exported only so the SWITCH wrapper can
 * delegate to it - ported as-is from
 * ReportServiceRequestAdapterService.normalizeManagementDomain(report-service-request-adapter.service.ts:281-296).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function normalizeManagementDomain(managementDomain: VCPM.ManagementDomain | undefined): any | undefined {
  if (!managementDomain) return managementDomain;

  try {
    // Deep clone to avoid mutating original data
    const normalizedDomain = JSON.parse(JSON.stringify(managementDomain));
    if (normalizedDomain?.configuration?.hardwareConfiguration) {
      normalizeHardwareConfiguration(normalizedDomain.configuration.hardwareConfiguration);
    }

    return normalizedDomain;
  } catch (error) {
    console.error('Error normalizing management domain:', error);
    return JSON.parse(JSON.stringify(managementDomain)); // Return clone without normalization on error
  }
}

/**
 * Normalizes hardware configuration by replacing driveSize with customDriveSize when "other" is
 * selected. Modifies the object in place since it's called on a deep clone. Module-private, not
 * exported - ported as-is from
 * ReportServiceRequestAdapterService.normalizeHardwareConfiguration (report-service-request-adapter.service.ts:302-314).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeHardwareConfiguration(hardwareConfig: any): void {
  if (!hardwareConfig) return;

  // If driveSize is "other" or "Other", replace it with customDriveSize
  if ((hardwareConfig.driveSize === 'other' || hardwareConfig.driveSize === 'Other') && hardwareConfig.customDriveSize != null) {
    hardwareConfig.driveSize = hardwareConfig.customDriveSize;
  }

  // Always remove customDriveSize from the payload as it's not expected by the PPT API
  if (hardwareConfig.hasOwnProperty('customDriveSize')) {
    delete hardwareConfig.customDriveSize;
  }
}

/**
 * Module-private (not part of the barrel's public API), exported only so the SWITCH wrapper can
 * delegate to it - ported as-is from
 * ReportServiceRequestAdapterService.roundToTwo (report-service-request-adapter.service.ts:316-319).
 */
export function roundToTwo(value: number | undefined): number | undefined {
  if (value == null || isNaN(value)) return value;
  return parseFloat(value.toFixed(2));
}

/**
 * Rounds a numeric value to two decimal places while preserving the original type.
 * - If the runtime value is a number, returns a rounded number.
 * - If it's a string containing a decimal number, returns a rounded string.
 * - Integers and non-numeric values pass through unchanged.
 *
 * Module-private (not part of the barrel's public API), exported only so the SWITCH wrapper can
 * delegate to it - ported as-is from
 * ReportServiceRequestAdapterService.roundAnyValue (report-service-request-adapter.service.ts:327-339).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function roundAnyValue(val: any): any {
  if (val == null) return val;
  if (typeof val === 'number') {
    if (isNaN(val) || Number.isInteger(val)) return val;
    return parseFloat(val.toFixed(2));
  }
  if (typeof val === 'string') {
    const num = parseFloat(val);
    if (isNaN(num) || Number.isInteger(num)) return val;
    return parseFloat(num.toFixed(2)).toString();
  }
  return val;
}

/**
 * Module-private, not exported - ported as-is from
 * ReportServiceRequestAdapterService.roundValueUnit (report-service-request-adapter.service.ts:341-344).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function roundValueUnit(obj: any): any {
  if (!obj || typeof obj !== 'object' || !('value' in obj)) return obj;
  return { ...obj, value: roundAnyValue(obj.value) };
}

/**
 * Module-private, not exported - ported as-is from
 * ReportServiceRequestAdapterService.roundClusterNumerics (report-service-request-adapter.service.ts:346-396).
 */
function roundClusterNumerics(cluster: PcsSizingEngineResponse.Cluster): PcsSizingEngineResponse.Cluster {
  if (cluster.clusterConfiguration) {
    const cc = cluster.clusterConfiguration;
    cc.rawStorage = roundValueUnit(cc.rawStorage);
    cc.effectiveUsableStorage = roundValueUnit(cc.effectiveUsableStorage);
    cc.rawCapacity = roundValueUnit(cc.rawCapacity);
    cc.rawStorageWithOverheads = roundValueUnit(cc.rawStorageWithOverheads);
    cc.operationsReserve = roundValueUnit(cc.operationsReserve);
    cc.rawCache = roundValueUnit(cc.rawCache);
    cc.calculatedCache = roundValueUnit(cc.calculatedCache);
    cc.metadataOverhead = roundValueUnit(cc.metadataOverhead);
    cc.vmsPerNodeSize = roundValueUnit(cc.vmsPerNodeSize);
  }
  if (cluster.serverConfiguration) {
    const sc = cluster.serverConfiguration;
    sc.rawStorage = roundValueUnit(sc.rawStorage);
    sc.rawStorageWithOverheads = roundValueUnit(sc.rawStorageWithOverheads);
    sc.usableStorage = roundValueUnit(sc.usableStorage);
    sc.rawCapacity = roundValueUnit(sc.rawCapacity);
    sc.requiredMemory = roundValueUnit(sc.requiredMemory);
    sc.physicalSpace = roundValueUnit(sc.physicalSpace);
    sc.vsanMemoryOverheadPerHost = roundValueUnit(sc.vsanMemoryOverheadPerHost);
    sc.requiredMemoryPerHost = roundValueUnit(sc.requiredMemoryPerHost);
    sc.operationsReservePerHost = roundValueUnit(sc.operationsReservePerHost);
  }
  if (cluster.diskSpaceUsageOverheads) {
    const dso = cluster.diskSpaceUsageOverheads;
    dso.rawStorage = roundValueUnit(dso.rawStorage);
    dso.rawCapacity = roundValueUnit(dso.rawCapacity);
    dso.remainingFreeSpace = roundValueUnit(dso.remainingFreeSpace);
    dso.objectOverhead = roundValueUnit(dso.objectOverhead);
    dso.fileSystemOverhead = roundValueUnit(dso.fileSystemOverhead);
    dso.perfTierOverhead = roundValueUnit(dso.perfTierOverhead);
  }
  if (cluster.workloadProfiles) {
    cluster.workloadProfiles = cluster.workloadProfiles.map((wp) => ({
      ...wp,
      slackSpace: roundValueUnit(wp.slackSpace),
      physicalReservation: roundValueUnit(wp.physicalReservation),
      rawStorageWithOverheads: roundValueUnit(wp.rawStorageWithOverheads),
    }));
  }
  if (cluster.resourceStats) {
    const rs = cluster.resourceStats;
    rs.cpu = roundValueUnit(rs.cpu);
    rs.iops = roundValueUnit(rs.iops);
    rs.capacity = roundValueUnit(rs.capacity);
    rs.memory = roundValueUnit(rs.memory);
  }
  return cluster;
}

/**
 * Determines the template version to use based on the project version.
 * Module-private (not part of the barrel's public API), exported only so the SWITCH wrapper can
 * delegate to it - ported as-is from
 * ReportServiceRequestAdapterService.getTemplateVersion (report-service-request-adapter.service.ts:403-419).
 */
export function getTemplateVersion(projectVersion: string | undefined): string {
  if (!projectVersion) {
    return '5.2.1'; // Default fallback
  }

  // Check if version starts with 9.0
  if (projectVersion.startsWith('9.0')) {
    return '9.0.0';
  }
  // Check if version starts with 9.1
  if (projectVersion.startsWith('9.1')) {
    return '9.1.0';
  }

  // Default to 5.2.1 for older versions
  return '5.2.1';
}
